import { FundPerformance } from '../../fund-performance/dto/FundPerformance';
import { reduceNumberSum } from '../../../util/common/math-util';
import { Organization } from '../../organization/dto/Organization';
import { RegExpService } from '../../../util/common/reg-exp.service';
import { CarryValues, NglTvpiForCarry } from "../../organization/components/organization-overview/utils";
import { OrgOverviewCalcsService } from "../../organization/components/organization-overview/org-overview-calcs.service";

export class InvestorContribution {
  uuid: string;
  investor: Investor;
  period: string; // is of type LocalDate (YYYY-MM-DD)
  contribution: number;
  distribution: number;
}

export interface InfoForIrrByPeriod {
  name: string;
  nav: number;
  netGainLossDiff: number;
  carryInterest: number;
}

export interface InfoForIrrByPeriodWrapper {
  infoByPeriods: InfoForIrrByPeriod[];
  fundUUID: string;
}

export class Investor {
  uuid: string;
  name: string;
  code: string;
  isGP: boolean;
  tracker: {
    commitment: number;
    called: number;
    predictionCalled: number;
  };
}


export interface IcPeriod {
  name: string;
  fp: FundPerformance;
  isOpened: boolean;
  allowDownloadExcel?: boolean;
  summary: InvestorContributionAdvanced;
  data: InvestorContributionWithCf[];
}

export interface IrrInfo {
  name: string;
  irr: number;
  irrAfterCarry: number;
}

export interface InvestorTracker {
  data: InvestorTrackerData[];
  total: InvestorCapitalCall;
}

export interface InvestorTrackerData {
  investor: Investor;
  isOpened: boolean;
  summary: InvestorCapitalCall;
  data: InvestorContribution[];
}

export interface InvestorCapitalCall {
  commitment: number;
  called: IcType;
  contribution: number;
  dueAdvance: IcType;
  uncalledCommitment: number;
}

export interface DistributionTracker {
  data: InvestorDistributionTrackerData[];
  total: InvestorDistributionSummary;
}

export interface InvestorDistributionSummary {
  commitment: number;
  distribution: number;
  dpi: number;
}

export interface InvestorDistributionTrackerData {
  investor: Investor;
  isOpened: boolean;
  summary: InvestorDistributionSummary;
  data: InvestorContribution[];
}

export interface IcType {
  current: number;
  prediction: number;
}

export class InvestorContributionAdvanced extends InvestorContribution {
  netCashflow: number;
  netGainLossDiff: number;
  irr?: number;
  irrCalculated?: number;
  irrAfterCarry?: number;
  irrCalculatedAfterCarry?: number;
  beforeCarry: NglTvpiForCarry;
  carry: CarryValues;
  afterCarry: NglTvpiForCarry;
}

export class InvestorContributionWithCf extends InvestorContribution {
  netCashflow?: number;
}

export class InvestorContributionUtil {
  calculateTotal(ics: InvestorContribution[], fp: FundPerformance, previousIc: InvestorContribution): InvestorContributionAdvanced {

    const contribution = reduceNumberSum(ics.map((ic) => ic.contribution), previousIc ? previousIc.contribution : 0);
    const distribution = reduceNumberSum(ics.map((ic) => ic.distribution), previousIc ? previousIc.distribution : 0);
    const nav = fp.navBeforeCarry;
    const netCashflow = contribution + distribution;
    const netGainLossCalculated = nav + netCashflow;
    const netGainLoss = (fp.distribution !== null && fp.navBeforeCarry && fp.contribution) ?
      (-fp.distribution - fp.carryDistribution + fp.navBeforeCarry - fp.contribution) : null;
    const netGainLossDiff = netGainLoss !== 0 ? (netGainLoss - netGainLossCalculated) : 0;
    return {
      uuid: null,
      investor: null,
      period: null,
      netGainLossDiff: netGainLossDiff,
      contribution: contribution,
      distribution: distribution,
      netCashflow: netCashflow,
      carry: OrgOverviewCalcsService.calcCarry(fp),
      beforeCarry: OrgOverviewCalcsService.calcBeforeCarry(fp),
      afterCarry: OrgOverviewCalcsService.calcAfterCarry(fp),
      irr: fp.netIrr,
      irrCalculated: fp.irrCalculated ? fp.irrCalculated : null,
      irrAfterCarry: fp.netIrrAfterCarry,
      irrCalculatedAfterCarry: fp.irrCalculatedAfterCarry ? fp.irrCalculatedAfterCarry : null,
    };
  }

  /**
   * <pre>
   * Creates a structure for the Investor contributions:
   * 1) separate the periods of fps.
   * 2) place the investor contributions in the fps period order.
   * E.x.:
   *
   * ic has periods: '2020-02-30', '2020-04-30', '2020-05-30', '2020-09-30'.
   * fp has periods: '2020-03-30' and '2020-06-30'.
   *
   * -------------------------------------
   *       FP period       IC period
   * -------------------------------------
   * | After 2020-06-30  |            |
   * |                   | 2020-09-30 |
   * | Till 2020-06-30   |            |
   * |                   | 2020-05-30 |
   * |                   | 2020-04-30 |
   * | Till 2020-03-30   |            |
   * |                   | 2020-02-30 |
   *
   * 3) calculate totals for fp periods.
   * 4) calculate accumulative new cashflow.
   *
   * Note: First the "till period" is calculated in the for-loop, and then the "after period" is calculated.
   * </pre>
   *
   * @param fps will be used for total data calculation.
   * @param ics must contain at least one period.
   * @return the structured data.
   * Each period has a summary of related ics and fp numbers, as well as data, which is ics per se.
   */
  prepareFundNetIrrData(fps: FundPerformance[], ics: InvestorContribution[]): IcPeriod[] {
    fps.sort((a, b) => new Date(b.period).getTime() - new Date(a.period).getTime());
    const icPeriods = fps.map((fp) =>
      ({ name: 'Till ' + String(fp.period), isOpened: false, summary: null, data: null, fp, allowDownloadExcel: true } as IcPeriod));
    const periods = fps.map((fp) => fp.period);
    const lastIndex = icPeriods.length - 1;
    for (let i = icPeriods.length - 1; i >= 0; i--) {
      const icPeriod = icPeriods[i];
      const currentPeriod = icPeriod.fp.period;
      const prevIcPeriod = i !== lastIndex ? icPeriods[i + 1] : null;
      const prevPeriod = periods.indexOf(currentPeriod) === lastIndex ? null : periods.at(periods.indexOf(currentPeriod) + 1);
      icPeriod.data = ics.filter((ic) => (ic.period <= String(currentPeriod) && (prevPeriod ? ic.period > String(prevPeriod) : true)));
      icPeriod.summary = new InvestorContributionUtil().calculateTotal(icPeriod.data, icPeriod.fp, prevIcPeriod?.summary);
    }
    icPeriods.unshift(
      { name: 'After ' + String(fps[0].period), isOpened: false, summary: null, data: null, fp: fps[0], allowDownloadExcel: false },
    );
    icPeriods[0].data = ics.filter((ic) => ic.period > String(fps[0].period));
    icPeriods[0].summary = new InvestorContributionUtil().calculateTotal(icPeriods[0].data, icPeriods[0].fp, icPeriods[1]?.summary);
    icPeriods[0].summary.irrCalculated = null;
    icPeriods[0].summary.irrCalculatedAfterCarry = null;
    if (!icPeriods[0].data.length) {
      icPeriods.splice(0, 1);
    } else {
      icPeriods[0].summary.irr = null;
      icPeriods[0].summary.irrAfterCarry = null;
    }
    icPeriods.forEach((ic) => {
      ic.data.sort((a, b) => b.period.localeCompare(a.period));
    });
    return this.calculateCF(icPeriods);
  };

  prepareCapitalCallData(ics: InvestorContribution[], investors: Investor[]): InvestorTracker {
    const trackers: InvestorTrackerData[] = [];
    this.prepareTrackers(ics, investors, trackers);

    return {
      data: trackers,
      total: this.prepareCCTotal(trackers, {
        current: investors[0].tracker.called ?? 0,
        prediction: investors[0].tracker.predictionCalled ?? 0,
      }),
    };
  }

  prepareDistributionData(ics: InvestorContribution[], investors: Investor[]): DistributionTracker {
    const trackers: InvestorDistributionTrackerData[] = [];
    this.prepareDistributionTrackers(ics, investors, trackers);

    return {
      data: trackers,
      total: this.prepareDistributionTotal(trackers),
    };
  }

  private prepareTrackers(ics: InvestorContribution[], investors: Investor[], trackers: InvestorTrackerData[]): void {
    investors.forEach((investor) => {
      const relatedICs = ics.filter((ic) => ic.investor.code === investor.code && ic.contribution);
      const commitment = investor.tracker.commitment ?? 0;
      const contribution = (reduceNumberSum(relatedICs.map((ic) => ic.contribution)));
      const called: IcType = {
        current: (investor.tracker.commitment ?? 0) * (investor.tracker.called ?? 0),
        prediction: (investor.tracker.commitment ?? 0) * (investor.tracker.predictionCalled ?? 0),
      };
      const dueAdvance: IcType = {
        current: -contribution - called.current,
        prediction: investor.tracker.predictionCalled ? Math.min(0, -contribution - called.current - called.prediction) : 0,
      };
      const tracker: InvestorTrackerData = {
        investor,
        data: relatedICs,
        summary: {
          commitment,
          called,
          contribution,
          dueAdvance,
          uncalledCommitment: commitment - called.prediction - called.current,
        },
        isOpened: false,
      };
      trackers.push(tracker);
    });
    trackers.sort((t1, t2) => t1.investor.code.localeCompare(t2.investor.code));
    trackers.forEach((tracker) => tracker.data
      .sort((a, b) => b.period.localeCompare(a.period)));
  }

  private prepareDistributionTrackers(ics: InvestorContribution[], investors: Investor[],
    trackers: InvestorDistributionTrackerData[]): void {
    investors.forEach((investor) => {
      const relatedDistributions = ics.filter((ic) => ic.investor.code === investor.code && ic.distribution);
      const relatedContribution = ics.filter((ic) => ic.investor.code === investor.code && ic.contribution);
      const commitment = investor.tracker.commitment ?? 0;
      const distribution = (reduceNumberSum(relatedDistributions.map((ic) => ic.distribution)));
      const contribution = (reduceNumberSum(relatedContribution.map((ic) => ic.contribution)));
      const dpi = contribution ? (distribution ?? 0) / (-contribution) : 0;
      const tracker: InvestorDistributionTrackerData = {
        investor,
        data: relatedDistributions,
        summary: {
          commitment,
          distribution,
          dpi,
        },
        isOpened: false,
      };
      trackers.push(tracker);
    });
    trackers.sort((t1, t2) => t1.investor.code.localeCompare(t2.investor.code));
    trackers.forEach((tracker) => tracker.data
      .sort((a, b) => b.period.localeCompare(a.period)));
  }

  private prepareCCTotal(trackers: InvestorTrackerData[], called: IcType): InvestorCapitalCall {
    const totalContribution = reduceNumberSum(trackers.map((tracker) => tracker.summary.contribution ?? 0));
    const totalCommitment = reduceNumberSum(trackers.map((tracker) => tracker.summary.commitment ?? 0));
    const totalCalled: IcType = {
      prediction: totalCommitment * called.prediction,
      current: totalCommitment * called.current,
    };
    const dueAdvance = {
      current: -totalContribution - totalCalled.current,
      prediction: reduceNumberSum(trackers.map((tracker) => tracker.summary.dueAdvance.prediction ?? 0)),
    };

    return {
      commitment: totalCommitment,
      called: totalCalled,
      contribution: totalContribution,
      dueAdvance,
      uncalledCommitment: totalCommitment - totalCalled.prediction - totalCalled.current,
    };
  }

  private prepareDistributionTotal(trackers: InvestorDistributionTrackerData[]): InvestorDistributionSummary {
    const totalDistribution = reduceNumberSum(trackers.map((tracker) => tracker.summary.distribution ?? 0));
    const totalCommitment = reduceNumberSum(trackers.map((tracker) => tracker.summary.commitment ?? 0));
    return {
      commitment: totalCommitment,
      distribution: totalDistribution,
      dpi: totalCommitment ? (totalDistribution ?? 0) / totalCommitment : 0,
    };
  }

  private calculateCF(icPeriods: IcPeriod[]): IcPeriod[] {
    let cf = 0;
    for (let i = icPeriods.length - 1; i >= 0; i--) {
      if (icPeriods[i].data.length > 0) {
        for (let j = icPeriods[i].data.length - 1; j >= 0; j--) {
          icPeriods[i].data[j].netCashflow = cf += icPeriods[i].data[j].contribution + icPeriods[i].data[j].distribution;
        }
      }
    }
    return icPeriods;
  }
}

/**
 * <pre>
 * Checks the Investor code on first 2 values: OOOO-FFF-IIII
 * Where OOOO is an Organization ID (id connected with portal, not entity ID).
 * Where FFF is an Fund ID (id connected with portal, not entity ID).
 * Where IIII is an Investor ID (can be extended).
 *
 * Fund can start with up to 2 empty zeroes.
 *
 * E.x.
 * Fund ID = 1; Org ID = 7700;
 * Legit | 7700-1-1 | 7700-01-1 | 7700-001-1 | 7700-01-1-1
 * Not legit | 4000-1-1 | 7700-0001-1 | 7700-1-
 * </pre>
 *
 * @param org with selected fund.
 * @param code of Investor.
 */
export const testCodeOnOrganization = (org: Organization, code: string): boolean => {
  const re = new RegExp(RegExpService.investorCode(org));
  return re.test(code);
};


export const CC_TRACKER_SORT = {
  byCode: (tracker: InvestorTrackerData): string => tracker.investor.code,
  byCommitment: (tracker: InvestorTrackerData): number => tracker.summary.commitment,
  byCalledCurrent: (tracker: InvestorTrackerData): number => tracker.summary.called.current,
  byContribution: (tracker: InvestorTrackerData): number => tracker.summary.contribution,
  byAdvancedDue: (tracker: InvestorTrackerData): number => tracker.summary.dueAdvance.current,
  byCalledPrediction: (tracker: InvestorTrackerData): number => tracker.summary.called.prediction,
  byCapitalToReceive: (tracker: InvestorTrackerData): number => tracker.summary.dueAdvance.prediction,
  byUncalledCommitment: (tracker: InvestorTrackerData): number => tracker.summary.uncalledCommitment,
};

export const DISTRIBUTION_TRACKER_SORT = {
  byCode: (tracker: InvestorDistributionTrackerData): string => tracker.investor.code,
  byCommitment: (tracker: InvestorDistributionTrackerData): number => tracker.summary.commitment,
  byDistribution: (tracker: InvestorDistributionTrackerData): number => tracker.summary.distribution,
  byDpi: (tracker: InvestorDistributionTrackerData): number => tracker.summary.dpi,
};
